import type { Call, Device } from '@twilio/voice-sdk';
import { useDeviceStore } from '../store/device';

/**
 * Place an outbound call (FR-51..FR-56).
 *
 *   const call = await dial('+61412345678');
 *
 * Wires the same accept/disconnect handlers the inbound flow uses, so the
 * activeCall store + CallControlsPane + sidebar status overlay all work
 * automatically. The In-call <-> Available status flips are handled
 * server-side (in /agent-voice + /outbound-completed) — no client ping.
 *
 * Throws if there's already an active call (Twilio Voice SDK rejects a
 * second concurrent connect from the same Device) or the device isn't
 * registered yet.
 */
export async function dial(to: string): Promise<Call> {
  const { device, activeCall } = useDeviceStore.getState();
  if (!device) throw new Error('Twilio Voice device not ready yet — please wait a moment.');
  if (activeCall) throw new Error('You already have an active call. Hang up first.');

  const normalised = normaliseAu(to);
  if (!normalised) throw new Error('Please enter a valid Australian phone number.');

  const call = await device.connect({ params: { To: normalised } });
  wireOutboundCall(device, call);
  return call;
}

function wireOutboundCall(_device: Device, call: Call): void {
  const { setActive, setIncoming } = useDeviceStore.getState();

  // Outbound: treat as activeCall immediately. Twilio's accept fires when
  // the destination picks up; we still want the controls pane visible
  // (with a "calling…" feel) before that.
  setActive(call);

  call.on('accept', () => {
    // already active; just confirm
    useDeviceStore.getState().setActive(call);
  });

  call.on('disconnect', () => {
    const s = useDeviceStore.getState();
    if (s.incomingCall === call) setIncoming(undefined);
    if (s.activeCall === call) setActive(undefined);
  });

  call.on('error', err => {
    console.error('outbound call error', err);
    const s = useDeviceStore.getState();
    if (s.activeCall === call) setActive(undefined);
  });
}

/**
 * Normalise free-form AU input to E.164 (`+61...`). Returns undefined if
 * the input can't plausibly be an AU phone number.
 */
export function normaliseAu(raw: string): string | undefined {
  const cleaned = raw.replace(/[\s\-()]/g, '');
  if (!cleaned) return undefined;
  let n = cleaned;
  if (n.startsWith('0061')) n = '+' + n.slice(2);          // 0061 4xx... → +614xx...
  else if (n.startsWith('61')) n = '+' + n;                // 61 4xx... → +614xx...
  else if (n.startsWith('0')) n = '+61' + n.slice(1);      // 04xx... → +614xx...
  if (!n.startsWith('+61')) return undefined;
  if (!/^\+61\d{9,10}$/.test(n)) return undefined;
  return n;
}
